import { createPdfWorker, loadPdfJs, pdfDocumentParams } from '@srm/ui/lib/pdfjs';

/** Больше — не открываем: весь файл читается в память и уходит в воркер. */
export const MAX_PDF_BYTES = 30 * 1024 * 1024;
/** Сложная страница (сканы, чертежи) не должна держать очередь бесконечно. */
const RENDER_TIMEOUT_MS = 15_000;
/** Предел масштаба: мелкая страница (визитка, чек) не раздувается в огромный растр. */
const MAX_SCALE = 2;

/** Рендеры по одному: каждый поднимает свой воркер и держит файл в памяти. */
let queue: Promise<unknown> = Promise.resolve();

const enqueue = <T>(job: () => Promise<T>): Promise<T> => {
    const run = queue.then(job, job);
    queue = run.catch(() => undefined);
    return run;
};

async function render(file: File, width: number): Promise<HTMLCanvasElement | null> {
    const { pdfjs, PdfWorker } = await loadPdfJs();
    const { worker, destroy } = createPdfWorker(pdfjs, PdfWorker);
    const task = pdfjs.getDocument({
        ...pdfDocumentParams(),
        data: new Uint8Array(await file.arrayBuffer()),
        worker,
    });
    const timer = setTimeout(() => void task.destroy(), RENDER_TIMEOUT_MS);
    try {
        const doc = await task.promise;
        const page = await doc.getPage(1);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: Math.min(MAX_SCALE, width / base.width) });
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));
        // у страницы PDF прозрачный фон — в тёмной теме «лист» стал бы тёмным
        await page.render({ canvas, viewport, background: 'white' }).promise;
        return canvas;
    } finally {
        clearTimeout(timer);
        await task.destroy();
        destroy();
    }
}

/** Первая страница PDF растром заданной ширины; `null` — рендер невозможен (нет воркеров). */
export function renderPdfFirstPage(file: File, width: number): Promise<HTMLCanvasElement | null> {
    if (typeof Worker === 'undefined' || file.size > MAX_PDF_BYTES) return Promise.resolve(null);
    return enqueue(() => render(file, width));
}
